import type { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { BaseQueryFn, MutationDefinition } from '@reduxjs/toolkit/query';

import {
  getDistinctBreakpointQuantitiesFromPriceList,
  getStoreCartBaseUnitPrice,
  getStoreCartPriceForQuantity,
  getStoreCartPriceFromPriceListOnly,
  resolveStoreCartFrozenBaseUnitPrice,
  STORE_CART_FRESH_PRINTS_MIN_QUANTITY_TIERS,
  STORE_CART_MIN_QUANTITY_TIERS,
  isFreshPrintsApparelBrand,
} from './storeCartPricing.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type {
  IQuoteBreakpoints,
  IQuoteResponse,
  QuoterPayload,
} from '@/common/interfaces/quoter.interface';
import { getCartPricingReqPayload, getDesignGroupQuantity } from '@/utilities/PricingUtils';
import type { StoreCartMoqShippingLane } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';

export interface IStoreCartQuoteOptions {
  storeShippingLane?: StoreCartMoqShippingLane;
  /** When true, always call quoter even if a cached price exists for the quantity. */
  forceRequote?: boolean;
}

type GetQuoteMutationFunction = MutationTrigger<
  MutationDefinition<
    { data: QuoterPayload },
    BaseQueryFn<{ url: string; method: string }>,
    never,
    IQuoteResponse[],
    'fetchQuoterApi'
  >
>;

export interface IStoreCartQuotePricing {
  priceList: IQuoteBreakpoints[];
  unitPrice: number;
  baseUnitPrice: number;
}

/** Same unit-price field selection as DesignProductDetails / design page */
export const getQuoterDisplayUnitPrice = (
  quoteBreakdown: IQuoteResponse,
  item: Pick<ICartItemDetail, 'designPricing'>
): number => {
  const hasDesignDiscount = (item.designPricing?.discount ?? 0) > 0;

  if (hasDesignDiscount && quoteBreakdown.finalPricePerUnitAfterDiscount != null) {
    return quoteBreakdown.finalPricePerUnitAfterDiscount;
  }

  if (quoteBreakdown.chargedPrice > 0) {
    return quoteBreakdown.chargedPrice;
  }

  return (
    quoteBreakdown.pricePerUnitWithCollegiate ??
    quoteBreakdown.finalPricePerUnit ??
    quoteBreakdown.pricePerUnit ??
    0
  );
};

const syncQuotedQuantityBreakpointPrice = (
  priceList: IQuoteBreakpoints[],
  quantity: number,
  unitPrice: number
): IQuoteBreakpoints[] => {
  if (unitPrice <= 0) {
    return priceList;
  }

  let matchedQuantity = false;
  const updatedPriceList = priceList.map((breakpoint) => {
    if (Number(breakpoint.quantity) !== quantity) {
      return breakpoint;
    }

    matchedQuantity = true;
    return { ...breakpoint, quantity, finalPricePerUnit: unitPrice };
  });

  if (matchedQuantity) {
    return updatedPriceList;
  }

  return [...updatedPriceList, { quantity, finalPricePerUnit: unitPrice }].sort(
    (left, right) => Number(left.quantity) - Number(right.quantity)
  );
};

export const fetchStoreCartItemQuote = async (
  item: ICartItemDetail,
  allCartItems: ICartItemDetail[],
  quantity: number,
  getQuote: GetQuoteMutationFunction,
  options: IStoreCartQuoteOptions = {}
): Promise<IStoreCartQuotePricing | null> => {
  const designGroupQuantity = getDesignGroupQuantity(item, allCartItems);
  const payload = getCartPricingReqPayload({ ...item, quantity }, designGroupQuantity, {
    storeShippingLane: options.storeShippingLane,
  });

  payload.apparels = payload.apparels.map((apparel) => ({
    ...apparel,
    forceBreakpointsForAnyPrintType: true,
  }));

  try {
    const response = await getQuote({ data: payload });

    if (!response || !('data' in response) || !response.data?.[0]) {
      return null;
    }

    const quoteBreakdown = response.data[0];
    const unitPrice = getQuoterDisplayUnitPrice(quoteBreakdown, item);
    const priceList = quoteBreakdown.breakpoints ?? [];
    const baseUnitPrice = getStoreCartBaseUnitPrice(priceList, unitPrice, item.apparelBrand);

    return {
      priceList,
      unitPrice,
      baseUnitPrice,
    };
  } catch {
    return null;
  }
};

type StoreCartFormFieldName =
  | `cartDetails.${number}.price`
  | `cartDetails.${number}.priceList`
  | `cartDetails.${number}.baseUnitPrice`
  | `cartDetails.${number}.strikedPricePerUnit`
  | `cartDetails.${number}.quantity`
  | `cartDetails.${number}.minQuant`;

interface IApplyStoreCartQuoteSetValue {
  (
    name: StoreCartFormFieldName,
    value: number | IQuoteBreakpoints[] | undefined,
    options?: { shouldValidate?: boolean }
  ): void;
}

/** True when the price list itself defines at least two breakpoint quantities with distinct prices. */
export const hasUsableStoreCartPriceList = (
  priceList: IQuoteBreakpoints[] | undefined,
  brand?: string
): boolean => {
  const quantities = getDistinctBreakpointQuantitiesFromPriceList(priceList, brand);

  if (quantities.length < 2 || !priceList?.length) {
    return false;
  }

  const distinctPrices = new Set(
    quantities
      .map(
        (quantity) =>
          priceList.find((breakpoint) => Number(breakpoint.quantity) === quantity)
            ?.finalPricePerUnit
      )
      .filter((price): price is number => typeof price === 'number' && price > 0)
  );

  return distinctPrices.size >= 2;
};

const updatePriceListPricesFromQuote = (
  existingPriceList: IQuoteBreakpoints[],
  quotePriceList: IQuoteBreakpoints[]
): IQuoteBreakpoints[] => {
  const quoteByQuantity = new Map(
    quotePriceList.map((breakpoint) => [Number(breakpoint.quantity), breakpoint])
  );

  return existingPriceList.map((existingBreakpoint) => {
    const quantity = Number(existingBreakpoint.quantity);
    const quoteBreakpoint = quoteByQuantity.get(quantity);

    return quoteBreakpoint
      ? { ...existingBreakpoint, ...quoteBreakpoint, quantity }
      : existingBreakpoint;
  });
};

const getDefaultMinQuantityTierSet = (brand?: string): Set<number> =>
  isFreshPrintsApparelBrand(brand)
    ? new Set<number>(STORE_CART_FRESH_PRINTS_MIN_QUANTITY_TIERS)
    : new Set<number>(STORE_CART_MIN_QUANTITY_TIERS);

const isDefaultMinQuantityTierSet = (
  priceList: IQuoteBreakpoints[] | undefined,
  brand?: string
): boolean => {
  const quantities = getDistinctBreakpointQuantitiesFromPriceList(priceList, brand);
  const defaultTierSet = getDefaultMinQuantityTierSet(brand);

  return quantities.length >= 2 && quantities.every((quantity) => defaultTierSet.has(quantity));
};

const hasCustomMinQuantityTiers = (
  priceList: IQuoteBreakpoints[] | undefined,
  brand?: string
): boolean => {
  const defaultTierSet = getDefaultMinQuantityTierSet(brand);

  return getDistinctBreakpointQuantitiesFromPriceList(priceList, brand).some(
    (quantity) => !defaultTierSet.has(quantity)
  );
};

/** Keep existing breakpoint tiers when a fresh quote would reduce selectable minimums. */
export const mergeStoreCartQuotePriceList = (
  existingPriceList: IQuoteBreakpoints[] | undefined,
  quotePriceList: IQuoteBreakpoints[],
  brand?: string
): IQuoteBreakpoints[] => {
  const existingUsable = hasUsableStoreCartPriceList(existingPriceList, brand);
  const quoteUsable = hasUsableStoreCartPriceList(quotePriceList, brand);

  if (!quoteUsable) {
    return existingPriceList?.length ? existingPriceList : quotePriceList;
  }

  if (!existingUsable) {
    return quotePriceList;
  }

  const resolvedExistingPriceList = existingPriceList ?? [];

  if (
    hasCustomMinQuantityTiers(resolvedExistingPriceList, brand) &&
    isDefaultMinQuantityTierSet(quotePriceList, brand)
  ) {
    return updatePriceListPricesFromQuote(resolvedExistingPriceList, quotePriceList);
  }

  const existingTierCount = getDistinctBreakpointQuantitiesFromPriceList(
    resolvedExistingPriceList,
    brand
  ).length;
  const quoteTierCount = getDistinctBreakpointQuantitiesFromPriceList(quotePriceList, brand).length;

  if (quoteTierCount < existingTierCount) {
    return updatePriceListPricesFromQuote(resolvedExistingPriceList, quotePriceList);
  }

  const existingQuantities = new Set(
    getDistinctBreakpointQuantitiesFromPriceList(resolvedExistingPriceList, brand)
  );
  const merged = updatePriceListPricesFromQuote(resolvedExistingPriceList, quotePriceList);

  quotePriceList.forEach((quoteBreakpoint) => {
    const quantity = Number(quoteBreakpoint.quantity);

    if (quantity > 0 && !existingQuantities.has(quantity)) {
      merged.push(quoteBreakpoint);
      existingQuantities.add(quantity);
    }
  });

  return merged.sort((left, right) => Number(left.quantity) - Number(right.quantity));
};

export const getStoreCartDesignGroupReferencePriceList = (
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[],
  fallbackCartDesign: ICartItemDetail,
  tierBrand: string | undefined
): IQuoteBreakpoints[] | undefined => {
  let bestPriceList: IQuoteBreakpoints[] | undefined;
  let bestTierCount = 0;

  groupFormIndices.forEach((groupItemIndex) => {
    const groupItem = cartDetails[groupItemIndex];
    const candidatePriceList = groupItem?.priceList?.length
      ? groupItem.priceList
      : fallbackCartDesign.priceList;

    const tierCount = getDistinctBreakpointQuantitiesFromPriceList(
      candidatePriceList,
      tierBrand
    ).length;

    if (tierCount > bestTierCount) {
      bestTierCount = tierCount;
      bestPriceList = candidatePriceList;
    }
  });

  if (bestPriceList?.length) {
    return bestPriceList;
  }

  return fallbackCartDesign.priceList?.length ? fallbackCartDesign.priceList : undefined;
};

export const resolveStoreCartDrawerReferencePriceList = (
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[],
  fallbackCartDesign: ICartItemDetail,
  tierBrand: string | undefined,
  pinnedPriceList?: IQuoteBreakpoints[] | null
): IQuoteBreakpoints[] | undefined => {
  const referenceFromForm = getStoreCartDesignGroupReferencePriceList(
    groupFormIndices,
    cartDetails,
    fallbackCartDesign,
    tierBrand
  );

  if (!pinnedPriceList?.length) {
    return referenceFromForm;
  }

  if (!referenceFromForm?.length) {
    return pinnedPriceList;
  }

  const pinnedTierCount = getDistinctBreakpointQuantitiesFromPriceList(
    pinnedPriceList,
    tierBrand
  ).length;
  const referenceTierCount = getDistinctBreakpointQuantitiesFromPriceList(
    referenceFromForm,
    tierBrand
  ).length;

  return pinnedTierCount >= referenceTierCount ? pinnedPriceList : referenceFromForm;
};

/** Instant tier price from cached breakpoints — no API call */
export const applyStoreCartDesignGroupTierPrices = (
  quantity: number,
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[],
  fallbackCartDesign: ICartItemDetail,
  designGroupUsesFreshPrintsMinimums: boolean,
  setValue: IApplyStoreCartQuoteSetValue
): void => {
  const formOptions = { shouldValidate: false };

  groupFormIndices.forEach((groupItemIndex) => {
    const groupItem = cartDetails[groupItemIndex];

    if (!groupItem) {
      return;
    }

    const groupPriceList = groupItem.priceList?.length
      ? groupItem.priceList
      : fallbackCartDesign.priceList ?? [];
    const groupTierBrand = designGroupUsesFreshPrintsMinimums
      ? 'Fresh Prints'
      : groupItem.apparelBrand;
    const apparelPrice = groupItem.apparelPrice ?? fallbackCartDesign.apparelPrice ?? 0;
    const groupFrozenBaseUnitPrice = resolveStoreCartFrozenBaseUnitPrice(
      groupItem.baseUnitPrice,
      apparelPrice,
      groupPriceList,
      groupTierBrand
    );

    if (groupItem.baseUnitPrice == null || groupItem.baseUnitPrice <= 0) {
      setValue(
        `cartDetails.${groupItemIndex}.baseUnitPrice`,
        groupFrozenBaseUnitPrice,
        formOptions
      );
    }

    const tierPrice = getStoreCartPriceForQuantity(
      quantity,
      groupPriceList,
      groupFrozenBaseUnitPrice,
      groupTierBrand
    );

    setValue(`cartDetails.${groupItemIndex}.quantity`, quantity, formOptions);
    setValue(`cartDetails.${groupItemIndex}.minQuant`, quantity, formOptions);
    setValue(`cartDetails.${groupItemIndex}.price`, tierPrice, formOptions);
  });
};

/** Store cart drawer tier updates — quantity only; clear unit price until quoter responds */
export const applyStoreCartDrawerDesignGroupTierPrices = (
  quantity: number,
  groupFormIndices: number[],
  cartDetails: ICartItemDetail[],
  _fallbackCartDesign: ICartItemDetail,
  setValue: IApplyStoreCartQuoteSetValue
): void => {
  const formOptions = { shouldValidate: false };

  groupFormIndices.forEach((groupItemIndex) => {
    const groupItem = cartDetails[groupItemIndex];

    if (!groupItem) {
      return;
    }

    setValue(`cartDetails.${groupItemIndex}.quantity`, quantity, formOptions);
    setValue(`cartDetails.${groupItemIndex}.minQuant`, quantity, formOptions);
    // Drop the previous breakpoint's unit price so the UI does not keep showing
    // e.g. a 12-tier price while minimum 16 is selected and still quoting.
    setValue(`cartDetails.${groupItemIndex}.price`, 0, formOptions);
    setValue(`cartDetails.${groupItemIndex}.strikedPricePerUnit`, undefined, formOptions);
  });
};

export interface IStoreCartQuotedItemFields {
  priceList: IQuoteBreakpoints[];
  baseUnitPrice: number;
  price: number;
  strikedPricePerUnit?: number;
  quantity: number;
  minQuant: number;
}

export const buildStoreCartQuotedItemFields = (
  quantity: number,
  quote: IStoreCartQuotePricing,
  brand: string | undefined,
  existingPriceList?: IQuoteBreakpoints[],
  existingUnitPrice?: number,
  existingStrikedPricePerUnit?: number
): IStoreCartQuotedItemFields => {
  const mergedPriceList = mergeStoreCartQuotePriceList(existingPriceList, quote.priceList, brand);
  const baseUnitPrice = getStoreCartBaseUnitPrice(mergedPriceList, quote.baseUnitPrice, brand);
  const displayPrice =
    quote.unitPrice > 0
      ? quote.unitPrice
      : getStoreCartPriceFromPriceListOnly(quantity, mergedPriceList) ?? quote.unitPrice;
  const resolvedPriceList =
    quote.unitPrice > 0
      ? syncQuotedQuantityBreakpointPrice(mergedPriceList, quantity, quote.unitPrice)
      : mergedPriceList;

  let strikedPricePerUnit = existingStrikedPricePerUnit;

  if (
    displayPrice > 0 &&
    typeof existingUnitPrice === 'number' &&
    existingUnitPrice > 0 &&
    existingUnitPrice !== displayPrice &&
    !(existingStrikedPricePerUnit && existingStrikedPricePerUnit > 0)
  ) {
    strikedPricePerUnit = existingUnitPrice;
  }

  return {
    priceList: resolvedPriceList,
    baseUnitPrice,
    price: displayPrice,
    strikedPricePerUnit,
    quantity,
    minQuant: quantity,
  };
};

export const applyStoreCartQuoteToCartItem = (
  item: ICartItemDetail,
  quantity: number,
  quote: IStoreCartQuotePricing,
  brand?: string
): ICartItemDetail => ({
  ...item,
  ...buildStoreCartQuotedItemFields(
    quantity,
    quote,
    brand,
    item.priceList,
    item.price,
    item.strikedPricePerUnit
  ),
});

export const applyStoreCartQuoteToFormItem = (
  itemFormIndex: number,
  quantity: number,
  quote: IStoreCartQuotePricing,
  brand: string | undefined,
  setValue: IApplyStoreCartQuoteSetValue,
  existingPriceList?: IQuoteBreakpoints[],
  existingUnitPrice?: number,
  existingStrikedPricePerUnit?: number
): void => {
  const formOptions = { shouldValidate: false };
  const quotedFields = buildStoreCartQuotedItemFields(
    quantity,
    quote,
    brand,
    existingPriceList,
    existingUnitPrice,
    existingStrikedPricePerUnit
  );

  if (quotedFields.strikedPricePerUnit != null) {
    setValue(
      `cartDetails.${itemFormIndex}.strikedPricePerUnit`,
      quotedFields.strikedPricePerUnit,
      formOptions
    );
  }

  setValue(`cartDetails.${itemFormIndex}.priceList`, quotedFields.priceList, formOptions);
  setValue(`cartDetails.${itemFormIndex}.baseUnitPrice`, quotedFields.baseUnitPrice, formOptions);
  setValue(`cartDetails.${itemFormIndex}.price`, quotedFields.price, formOptions);
  setValue(`cartDetails.${itemFormIndex}.quantity`, quotedFields.quantity, formOptions);
  setValue(`cartDetails.${itemFormIndex}.minQuant`, quotedFields.minQuant, formOptions);
};
